const ALGOLIA_INSIGHTS_ENDPOINT = "https://insights.algolia.io/1/events";
const ALGOLIA_USER_TOKEN_KEY = "lyleAlgoliaUserToken";

let fallbackUserToken = "";

export function algoliaUserToken(config = {}) {
  const customerId = algoliaAuthenticatedUserToken(config);

  if (customerId) {
    return customerId;
  }

  try {
    const existing = localStorage.getItem(ALGOLIA_USER_TOKEN_KEY);

    if (existing) {
      return existing;
    }

    const token = createUserToken();
    localStorage.setItem(ALGOLIA_USER_TOKEN_KEY, token);
    return token;
  } catch {
    fallbackUserToken ||= createUserToken();
    return fallbackUserToken;
  }
}

export function algoliaAuthenticatedUserToken(config = {}) {
  const customerId = config.customerId || config.customer?.id || "";
  return customerId ? String(customerId) : "";
}

export function createMoneyFormatter({ currencyCode, language }) {
  try {
    return new Intl.NumberFormat(language || undefined, {
      style: "currency",
      currency: currencyCode,
    });
  } catch {
    return null;
  }
}

export function escapeAttribute(value) {
  return escapeHtml(value).replace(/`/g, "&#096;");
}

export function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function formatMoney(
  value,
  { currencySymbol = "", formatter = null } = {},
) {
  const amount = Number(value || 0);

  if (formatter) {
    return formatter.format(amount);
  }

  return `${currencySymbol}${amount.toFixed(2)}`;
}

export function trackAlgoliaProductClick(config, index, element) {
  return trackAlgoliaProductEvent({
    config,
    element,
    eventName: "Product Clicked",
    eventType: "click",
    index,
  });
}

export function trackAlgoliaProductConversion(config, index, element, data = {}) {
  return trackAlgoliaProductEvent({
    config,
    data,
    element,
    eventName: "Product Added to Cart",
    eventType: "conversion",
    index,
  });
}

export function trackAlgoliaProductAddToCart(config, index, data = {}) {
  return trackAlgoliaProductEvent({
    config,
    data,
    element: data.element || null,
    eventName: "Product Added to Cart",
    eventType: "conversion",
    index,
  });
}

export function hiddenSalePrice(tags, currency = "GBP") {
  return hiddenSaleLineItemProperties(tags)[
    `_hidden_sale_price_${currency.toLowerCase()}`
  ] ?? null;
}

export function hiddenSaleLineItemProperties(tags = []) {
  if (
    typeof document === "undefined" ||
    !document.cookie.includes(`${encodeURIComponent("ls-can-view-hidden-sale")}=`)
  ) {
    return {};
  }

  return tags.reduce((properties, tag) => {
    if (!tag.startsWith("vip-price-")) return properties;

    const separator = tag.indexOf(":");
    const currency = tag.slice(10, separator).trim();
    const price = Number(tag.slice(separator + 1).trim());

    if (separator > 10 && currency && Number.isFinite(price)) {
      properties[`_hidden_sale_price_${currency}`] = price;
    }

    return properties;
  }, {});
}

export function imageUrl(image, width) {
  const src = image?.src || "";

  if (!src) {
    return "";
  }

  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set("width", String(width));
    return url.toString();
  } catch {
    const separator = src.includes("?") ? "&" : "?";
    return `${src}${separator}width=${width}`;
  }
}

export function isSameProduct(productA, productB) {
  return (
    String(productA?.id || "") === String(productB?.id || "") ||
    (Boolean(productA?.handle) && productA.handle === productB?.handle)
  );
}

export function isWhiteSwatch(color) {
  const value = String(color || "")
    .trim()
    .toLowerCase();

  return value === "#fff" || value === "#ffffff";
}

export function parseSwatchColour(colour) {
  if (!colour) return null;

  const normalised = String(colour).trim().toLowerCase();

  if (normalised.startsWith("#")) {
    let hex = normalised.slice(1);

    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((character) => character + character)
        .join("");
    }

    if (hex.length !== 6) return null;

    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  const rgbMatch = normalised.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (!rgbMatch) return null;

  return {
    r: parseInt(rgbMatch[1], 10),
    g: parseInt(rgbMatch[2], 10),
    b: parseInt(rgbMatch[3], 10),
  };
}

export function getRelativeLuminance({ r, g, b }) {
  const channel = (value) => {
    const normalised = value / 255;
    return normalised <= 0.03928
      ? normalised / 12.92
      : ((normalised + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function isLightSwatchColour(colour) {
  const rgb = parseSwatchColour(colour);
  if (!rgb) return false;
  return getRelativeLuminance(rgb) >= 0.7;
}

/**
 * Whether a colourway product is sold out for the current size selection.
 * Fully sold-out colours are marked before a size is selected; once a size is
 * selected, sold-out is based on that size only (via sizeStock, sizesAvailable,
 * or variants) — matching legacy colour-swatches behaviour.
 */
export function isSwatchSoldOut(swatch, selectedOptionValues = []) {
  if (!swatch) return false;

  const selectedSize = selectedSizeValue(swatch, selectedOptionValues);

  if (selectedSize) {
    const stock = sizeStockValue(swatch, selectedSize);
    if (stock !== undefined) return Number(stock) === 0;

    if (Array.isArray(swatch.sizesAvailable)) {
      if (swatch.sizesAvailable.length === 0) {
        return hasSizeChart(swatch) || isProductUnavailable(swatch);
      }
      return !swatch.sizesAvailable.includes(selectedSize);
    }

    const matchingVariant = findVariantForSize(swatch, selectedSize);
    if (matchingVariant) return !isVariantAvailable(matchingVariant);

    if (hasSizeInventoryData(swatch)) return true;

    return isProductUnavailable(swatch);
  }

  if (isProductUnavailable(swatch)) return true;
  if (isSizeStockFullySoldOut(swatch)) return true;
  if (isSizesAvailableEmpty(swatch)) return true;

  const variants = swatch.variants;
  if (variants?.length && variants.every((variant) => !isVariantAvailable(variant))) {
    return true;
  }

  return false;
}

function isProductUnavailable(swatch) {
  const value = swatch?.available;
  return value === false || value === "false" || value === 0;
}

function isVariantAvailable(variant) {
  if (!variant) return false;

  const value = variant.available;
  if (value === false || value === "false" || value === 0) return false;
  if (value === true || value === "true") return true;

  if (typeof variant.inventory_quantity === "number") {
    return variant.inventory_quantity > 0;
  }

  return Boolean(value);
}

function selectedSizeValue(swatch, selectedOptionValues) {
  if (!selectedOptionValues?.length) return "";

  const sizeStock = swatch?.sizeStock;
  if (sizeStock && typeof sizeStock === "object") {
    for (const value of selectedOptionValues) {
      if (Object.prototype.hasOwnProperty.call(sizeStock, value)) {
        return value;
      }
    }
  }

  const sizeChart = [
    ...(Array.isArray(swatch?.sizes) ? swatch.sizes : []),
    ...(Array.isArray(swatch?.sizesAvailable) ? swatch.sizesAvailable : []),
  ];

  for (const value of selectedOptionValues) {
    if (sizeChart.includes(value)) return value;
  }

  for (const variant of swatch?.variants || []) {
    const values = variantOptionValues(variant);
    for (const value of selectedOptionValues) {
      if (values.includes(value)) return value;
    }
  }

  return selectedOptionValues[0] || "";
}

function sizeStockValue(swatch, size) {
  const sizeStock = swatch?.sizeStock;
  if (!sizeStock || typeof sizeStock !== "object") return undefined;
  if (!Object.prototype.hasOwnProperty.call(sizeStock, size)) return undefined;
  return sizeStock[size];
}

function isSizeStockFullySoldOut(swatch) {
  const sizeStock = swatch?.sizeStock;
  if (!sizeStock || typeof sizeStock !== "object") return false;

  const stocks = Object.values(sizeStock);
  return stocks.length > 0 && stocks.every((stock) => Number(stock) === 0);
}

function isSizesAvailableEmpty(swatch) {
  if (!Array.isArray(swatch?.sizesAvailable)) return false;
  if (swatch.sizesAvailable.length > 0) return false;
  return hasSizeChart(swatch);
}

function hasSizeChart(swatch) {
  if (Array.isArray(swatch?.sizes) && swatch.sizes.length > 0) return true;
  if (swatch?.sizeStock && Object.keys(swatch.sizeStock).length > 0) return true;
  if (swatch?.variants?.length) return true;
  return false;
}

function hasSizeInventoryData(swatch) {
  if (swatch?.sizeStock && Object.keys(swatch.sizeStock).length > 0) return true;
  if (swatch?.sizes?.length || swatch?.sizesAvailable?.length) return true;
  if (swatch?.variants?.length) return true;
  return false;
}

function findVariantForSize(swatch, size) {
  return (swatch?.variants || []).find((variant) => {
    const label = variantLabel(variant);
    if (label === size) return true;
    return variantOptionValues(variant).includes(size);
  });
}

function variantOptionValues(variant) {
  if (variant?.selectedOptions?.length) {
    return variant.selectedOptions.map((option) => option.value).filter(Boolean);
  }

  if (Array.isArray(variant?.options)) {
    return variant.options.filter(Boolean);
  }

  if (variant?.option1) {
    return [variant.option1, variant.option2, variant.option3].filter(Boolean);
  }

  if (variant?.title && variant.title !== "Default Title") {
    return variant.title.split(" / ").map((part) => part.trim());
  }

  const label = variantLabel(variant);
  return label ? [label] : [];
}

export function metafieldValue(product, name) {
  return (
    product?.metafields?.find(
      (field) => field?.name === name || field?.key === name,
    )?.value || ""
  );
}

export function numericId(id) {
  return String(id || "")
    .split("/")
    .pop();
}

export function parseJson(value) {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function variantLabel(variant) {
  return (
    variant?.displayOption ||
    variant?.selectedOptions?.[0]?.value ||
    variant?.title ||
    ""
  );
}

function createUserToken() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

async function trackAlgoliaProductEvent({
  config,
  data = {},
  element,
  eventName,
  eventType,
  index,
}) {
  const objectID = data.objectID || element?.dataset.objectId;
  const resolvedIndex =
    data.index ||
    element?.closest("[data-product-card]")?.dataset.algoliaIndex ||
    index;

  if (
    !objectID ||
    !resolvedIndex ||
    !config?.algolia?.appId ||
    !config.algolia.apiKey
  ) {
    return;
  }

  const event = {
    eventName,
    eventType,
    index: resolvedIndex,
    objectIDs: [objectID],
    userToken: algoliaUserToken(config),
  };

  const authenticatedUserToken = algoliaAuthenticatedUserToken(config);

  if (authenticatedUserToken) {
    event.authenticatedUserToken = authenticatedUserToken;
  }

  const queryID = data.queryID || element?.dataset.queryId;
  const quantity = Number(data.quantity || 1);
  const price = normaliseAlgoliaPrice(data.price);
  const currency = data.currency || config.market?.currencyCode;

  if (queryID) {
    event.queryID = queryID;
  }

  if (eventType === "click" && Number(element?.dataset.position) > 0) {
    event.positions = [Number(element.dataset.position)];
  }

  if (eventType === "conversion") {
    event.eventSubtype = data.eventSubtype || "addToCart";

    if (Number.isFinite(price)) {
      event.value = price * quantity;
      event.objectData = [{
        price,
        quantity,
        ...(queryID ? { queryID } : {}),
      }];
    }

    if (currency) {
      event.currency = currency;
    }
  }

  try {
    await fetch(ALGOLIA_INSIGHTS_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Algolia-API-Key": config.algolia.apiKey,
        "X-Algolia-Application-Id": config.algolia.appId,
      },
      body: JSON.stringify({ events: [event] }),
      keepalive: true,
    });
  } catch {
    return;
  }

  if (eventType === "conversion") {
    void saveAlgoliaCartEvent({
      config,
      currency,
      index: resolvedIndex,
      objectID,
      queryID,
    });
  }
}

function normaliseAlgoliaPrice(value) {
  const amount = Number(value);

  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  return amount > 1000 ? amount / 100 : amount;
}

async function saveAlgoliaCartEvent({
  config,
  currency,
  index,
  objectID,
  queryID,
}) {
  const cart = await fetchCart(config);
  const token = cart?.token;

  if (!token) {
    return;
  }

  try {
    const key = `uwp-cart-${token}`;
    const existing = JSON.parse(localStorage.getItem(key) || "[]");

    existing.push({
      index,
      objectIDs: [objectID],
      currency,
      queryID: queryID || null,
    });

    localStorage.setItem(key, JSON.stringify(existing));
  } catch {
    return;
  }
}

async function fetchCart(config) {
  try {
    const root = config?.market?.rootUrl || window.Shopify?.routes?.root || "/";
    const response = await fetch(`${String(root).replace(/\/+$/, "")}/cart.js`, {
      credentials: "same-origin",
    });

    return response.ok ? response.json() : null;
  } catch {
    return null;
  }
}
